/**
 * API Fetch Strategy
 * Fetches data from REST API endpoints
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseDataFetchStrategy } from './data-fetch-strategy.interface';
import { IDataFetchContext } from '../models';
import { DataFetchType } from '../constants';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class ApiFetchStrategy extends BaseDataFetchStrategy {
  constructor(private http: HttpClient) {
    super();
  }
  
  canHandle(dataFetchType: string): boolean {
    return dataFetchType?.toLowerCase() === DataFetchType.API;
  }
  
  getName(): string {
    return 'API Fetch Strategy';
  }
  
  override validate(context: IDataFetchContext): boolean {
    if (!super.validate(context)) {
      return false;
    }
    
    if (!context.field.dataFetchFrom) {
      this.logWarning('No API endpoint specified in dataFetchFrom', context);
      return false;
    }
    
    // If field has dependency, check if dependent value exists
    if (context.field.dependentOn && !this.hasDependentValue(context)) {
      return false;
    }
    
    return true;
  }
  
  execute(context: IDataFetchContext): Observable<any> {
    if (!this.validate(context)) {
      return of([]);
    }
    
    try {
      // Build the API URL - just like legacy formatAPIURL
      let apiUrl = context.field.dataFetchFrom!;
      
      // Replace placeholders (legacy behavior)
      apiUrl = this.replacePlaceholders(apiUrl, context);
      
      // Add base URL if not absolute
      if (!apiUrl.startsWith('http')) {
        apiUrl = environment.getApiUrl(apiUrl);
      }
      
      // ✅ LEGACY BEHAVIOR: The URL template defines ALL parameters
      // Don't add extra query parameters - just use the URL as-is after placeholder replacement
      
      this.logInfo(`Fetching from API: ${apiUrl}`, context);
      
      // Make API call without adding extra params
      return this.http.get(apiUrl).pipe(
        map((response: any) => this.normalizeResponse(response)),
        catchError((error) => {
          this.logError(error, context);
          return of([]);
        })
      );
      
    } catch (error) {
      this.logError(error, context);
      return of([]);
    }
  }
  
  /**
   * Build HTTP query parameters
   */
  private buildQueryParams(context: IDataFetchContext): HttpParams {
    let params = new HttpParams();
    
    // Add company and branch
    if (context.companyId) {
      params = params.set('CompanyId', context.companyId.toString());
    }
    
    if (context.branchId) {
      params = params.set('BranchId', context.branchId.toString());
    }
    
    // Add search term if provided
    if (context.searchTerm) {
      params = params.set('searchTerm', context.searchTerm);
      params = params.set('FilterValue', context.searchTerm);
    }
    
    // Add master ID if provided
    if (context.masterId) {
      params = params.set('masterId', context.masterId.toString());
    }
    
    // Add dependent field value if exists
    if (context.field.dependentOn) {
      const dependentValue = this.getDependentValue(context);
      if (dependentValue !== null && dependentValue !== undefined) {
        const paramValue = typeof dependentValue === 'object'
          ? dependentValue[context.field.displayPropertyName || 'id'] || dependentValue.id || ''
          : dependentValue;
        params = params.set('DependentOnId', paramValue.toString());
      }
    }
    
    return params;
  }
  
  /**
   * Normalize API response to consistent format
   */
  private normalizeResponse(response: any): any[] {
    console.log('[ApiFetchStrategy] Raw API response:', response);
    
    // If already an array, return it
    if (Array.isArray(response)) {
      console.log('[ApiFetchStrategy] Response is array, returning', response.length, 'items');
      return response;
    }
    
    // Check common response patterns
    if (response.data && Array.isArray(response.data)) {
      console.log('[ApiFetchStrategy] Extracting from response.data:', response.data.length, 'items');
      return response.data;
    }
    
    if (response.result && Array.isArray(response.result)) {
      console.log('[ApiFetchStrategy] Extracting from response.result:', response.result.length, 'items');
      return response.result;
    }
    
    if (response.items && Array.isArray(response.items)) {
      console.log('[ApiFetchStrategy] Extracting from response.items:', response.items.length, 'items');
      return response.items;
    }
    
    // If response is an object, wrap it in array
    if (typeof response === 'object' && response !== null) {
      console.log('[ApiFetchStrategy] Wrapping single object in array');
      return [response];
    }
    
    // Return empty array for other cases
    console.warn('[ApiFetchStrategy] No data found in response, returning empty array');
    return [];
  }
}
